import { SmartButton } from '@tunasong/icons'
import { graphHooks } from '@tunasong/graph-lib/react'
import type { IconProps } from '@tunasong/icons'
import {
  AccountTree,
  Activities,
  Brain as AgentIcon,
  Context,
  Debug,
  Folder,
  Share as Graph,
  Pool,
  Settings,
  Suggestion,
} from '@tunasong/icons'
import { isCollabEntity, isNote, isPersistedEntity, logger, matchAny } from '@tunasong/models'
import { ChatBadge, useChat } from '@tunasong/plugin-chat/ui'
import { features, useThunkDispatch } from '@tunasong/redux'
import type { Entity, Persisted } from '@tunasong/schemas'
import { isChat, isFolder, isPage, isSong } from '@tunasong/schemas'
import { useTunaMediaQuery, type SidebarHandler } from '@tunasong/ui-lib'
import {
  Divider,
  HBox,
  Typography,
  VBox,
  isDevelopment,
  useElementRef,
  useSidebar,
  ToggleButton,
} from '@tunasong/ui-lib'
import cn from 'classnames'
import type { FC, HTMLAttributes } from 'react'
import { Fragment, useCallback, useEffect, useMemo, useRef } from 'react'
import invariant from 'tiny-invariant'
import sidebarStyles from './sidebar.module.css'
import { SidebarActivities } from './sidebar-activities.js'
import { SidebarAgents } from './sidebar-agents.js'
import { SidebarChat } from './sidebar-chat.js'
import { SidebarContent } from './sidebar-content.js'
import { SidebarContext } from './sidebar-context.js'
import { SidebarDebug } from './sidebar-debug.js'
import { SidebarEntity } from './sidebar-entity.js'
import { SidebarEvents } from './sidebar-events.js'
import { SidebarGraph } from './sidebar-graph.js'
import { SidebarPool } from './sidebar-pool.js'
import { SidebarSettings } from './sidebar-settings.js'
import { SidebarSuggestion } from './sidebar-suggestion.js'
import { EntityNotes, NotesBadge } from '@tunasong/editor'
import type { Note } from '@tunasong/models'
import { resolveRefTool } from './resolve-ref-tool.js'
import { shouldHandleRef } from './should-handle-ref.js'

export interface SidebarProps extends HTMLAttributes<HTMLDivElement> {
  className?: string
  entity?: Persisted<Entity>
}

const Sidebar: FC<SidebarProps> = props => {
  const { entity, className, style, ...restProps } = props

  const { activeTool, setActiveTool } = useSidebar()

  const { activeComments, isLoading: isChatLoading } = useChat({ element: entity })

  // Personal notes: the My Notes badge count, and `?ref=<noteId>` resolution below. Shares the
  // RTK Query cache with EntityNotes, so opening the panel does not refetch.
  const { entities: notes } = graphHooks.useEntityChildren<Note>({ parentId: entity?.id, filter: 'note' })

  /**
   * `?ref=<id>` in the URL opens Chat for a comment, or My Notes focused on one of this entity's
   * notes (a note's backlink, GH #1579). Each ref is handled once: the lists refetch, and a
   * re-run must not re-open a note the user has since collapsed. The guard is set only when a
   * tool resolves, so a ref that arrives before its list has loaded fires once the list does.
   */
  const ref = useElementRef()
  const dispatch = useThunkDispatch()
  const handledRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (!shouldHandleRef({ ref, handled: handledRef.current })) {
      return
    }
    const tool = resolveRefTool({
      ref,
      commentIds: activeComments.map(c => c.id),
      noteIds: notes.map(n => n.id),
    })
    if (!(tool && ref)) {
      return
    }
    handledRef.current = ref
    setActiveTool(tool)
    if (tool === 'My Notes') {
      dispatch(features.ui.actions.setPendingNoteScroll(true))
      dispatch(features.ui.actions.setPendingAnchoredNoteId(ref))
    }
  }, [activeComments, dispatch, notes, ref, setActiveTool])

  /** If activeComments change, we automatically open the chat tool */
  const prevActiveComments = useRef<number>(0)
  useEffect(() => {
    if (prevActiveComments.current > 0 && prevActiveComments.current < activeComments.length && !isChatLoading) {
      logger.debug('Setting active', prevActiveComments, activeComments)
      setActiveTool('Chat')
    }
    prevActiveComments.current = activeComments.length
  }, [activeComments, isChatLoading, setActiveTool])

  // 250ms balances two concerns:
  //  - Free-text fields (ElementForm Name/Description) coalesce per-keystroke
  //    bursts so we don't fire one API call per character.
  //  - Discrete-choice fields (ScaleSelect, MeterSelect, EnumSelect type)
  //    propagate to the editor (rootElement.scale → chord suggestions) within
  //    a normal interaction frame, not seconds later.
  // Previously 3s (from e06d8637b "Debounce tweaks", 2023); that left the
  // InlineCaption Roman-numeral parenthetical racing the playwright timeout
  // and going missing entirely on the live UI for ~3s after every scale
  // change (GH #1362).
  const handleUpdate = graphHooks.useEntityUpdate({ debounceDelay: 250 })
  const handleEntityChange = (partial: Partial<Entity>) => {
    invariant(isPersistedEntity(entity), 'Entity to be updated must be an element')
    handleUpdate(entity.id, partial)
  }

  const numComments = activeComments.length
  const ChatIcon = useMemo(
    () => (props: IconProps) => <ChatBadge numComments={numComments} {...props} />,
    [numComments]
  )

  const numNotes = notes.length
  const NotesIcon = useMemo(() => (props: IconProps) => <NotesBadge count={numNotes} {...props} />, [numNotes])

  const tools: SidebarHandler[] = [
    {
      name: `Settings`,
      Icon: Settings,
      Component: SidebarSettings,
      disabled: () => false,
    },
    {
      name: 'Chat',
      Icon: ChatIcon,
      Component: SidebarChat,
      disabled: () => isChat(entity),
    },
    {
      name: 'Activities',
      description: 'Summary of recent activities',
      Icon: Activities,
      Component: SidebarActivities,
      disabled: () => false,
    },
    {
      name: 'Agents',
      description: 'Chat with the agents attached to this entity. Accept or reject their proposals from the same panel.',
      Icon: AgentIcon,
      Component: SidebarAgents,
      // Mirrors the agent plugin's supportedTypes set (song / page / folder). The
      // SidebarAgents body renders an empty state when the cache is cold or no
      // agents are attached, covering the children-not-yet-loaded window.
      disabled: (entity: Entity) => !matchAny(isSong, isPage, isFolder)(entity),
    },
    {
      name: 'My Notes',
      description:
        'Personal notes — only you can see them. Capo positions, tricky transitions, lyric cues. Notes are also surfaced in performance mode.',
      Icon: NotesIcon,
      Component: EntityNotes,
      // Notes of a note are not a thing (GH #1579)
      disabled: (entity: Entity) => isNote(entity),
    },
    {
      name: 'Event Commands',
      description:
        'Commands to run for different events. For example, you can run a command when a the page is opened.',
      Icon: SmartButton,
      Component: SidebarEvents,
      disabled: () => false,
    },

    {
      name: 'Pool',
      description: 'The Pool contains media that belongs to this item. Select media to insert it into the page.',
      Icon: Pool,
      Component: SidebarPool,
      disabled: (entity: Entity) => !matchAny(isSong, isPage)(entity),
    },
    {
      name: 'Context',
      description: 'The context shows the musical context. If you are in an editor your position will matter.',
      Icon: Context,
      Component: SidebarContext,
      disabled: (entity: Entity) => !isSong(entity),
    },
    {
      name: 'Suggestions',
      description: 'Suggestions for chords, notes or scales based on your current context',
      Icon: Suggestion,
      Component: SidebarSuggestion,
      disabled: (entity: Entity) => !isSong(entity),
    },

    // {
    //   name: 'Record',
    //   description: 'Record your performance',
    //   Icon: Microphone,
    //   Component: SidebarRecord,
    //   // @todo move these into the plugins
    //   disabled: (entity: Entity) => !(isSong(entity) || isPage(entity) || isFolder(entity)),
    // },
    // {
    //   name: 'Guitar',
    //   description: 'Guitar tools',
    //   Icon: Guitar,
    //   Component: SidebarGuitar,
    //   disabled: (entity: Entity) => !isSong(entity),
    // },
    isDevelopment()
      ? {
          name: 'Debug',
          description: 'Debug information',
          Icon: Debug,
          Component: SidebarDebug,
          disabled: (entity: Entity) => !isCollabEntity(entity),
        }
      : null,
    isDevelopment()
      ? {
          name: 'Graph',
          description: 'Show the outgoing and incoming links in the content graph.',
          Icon: Graph,
          Component: SidebarGraph,
          disabled: (entity: Entity) => !entity,
        }
      : null,
    isDevelopment()
      ? {
          name: 'Debug Content',
          description: 'All child content',
          Icon: Folder,
          Component: SidebarContent,
          disabled: () => false,
        }
      : null,
    isDevelopment()
      ? {
          name: 'Entity Content',
          description: 'Entity Details',
          Icon: AccountTree,
          Component: SidebarEntity,
          disabled: () => false,
        }
      : null,
  ].filter(Boolean) as SidebarHandler[]

  /** When the entity is not set we collapse the toolbar */
  useEffect(() => {
    if (!entity) {
      setActiveTool(null)
      return
    }
    /** If there is an active tool, and it's disabled, we also collapse */
    if (activeTool && tools.find(t => t.name === activeTool)?.disabled(entity)) {
      setActiveTool(null)
    }
  }, [activeTool, entity, setActiveTool, tools])

  const toggleActive = useCallback(
    (num: number) => () => {
      if (tools.findIndex(tool => tool.name === activeTool) === num) {
        setActiveTool(null)
      } else {
        setActiveTool(tools[num].name)
      }
    },
    [activeTool, setActiveTool, tools]
  )

  const xl = useTunaMediaQuery('(min-width:1536px)')
  const tiny = useTunaMediaQuery('(max-width:599.95px)')

  const handler = activeTool !== null ? tools.find(t => t.name === activeTool) : null
  const ActiveComponent = handler ? handler.Component : null
  const showSidebarIcons = !tiny || (tiny && ActiveComponent)

  // 100vw is a hack to make the sidebar full width on mobile
  const containerWidth = tiny && activeTool ? '100vw' : 'auto'

  const toolWidth = activeTool ? (xl ? 450 : 350) : 0

  return (
    <HBox
      className={cn(sidebarStyles.container, className)}
      style={{
        ...style,
        width: containerWidth,
      }}
      {...restProps}
    >
      <VBox
        className={sidebarStyles.toolPanel}
        style={{
          width: toolWidth,
          opacity: activeTool ? 1 : 0,
          paddingBottom: activeTool ? 16 : 0,
          paddingLeft: activeTool ? 8 : 0,
          paddingRight: activeTool ? 8 : 0,
        }}
      >
        {ActiveComponent && entity && (
          <Fragment key={entity.id}>
            <div>
              <Divider>{handler?.name}</Divider>
            </div>
            {handler?.description ? (
              <>
                <Typography variant="caption" gutterBottom>
                  {handler?.description}
                </Typography>
              </>
            ) : null}
            <ActiveComponent key={entity?.id} entity={entity} onEntityChange={handleEntityChange} />
          </Fragment>
        )}
      </VBox>
      {showSidebarIcons ? (
        <div className={sidebarStyles.iconColumn}>
          {tools.map(({ name, disabled, Icon }, idx) => (
            <ToggleButton
              key={idx}
              className={sidebarStyles.toggleButton}
              style={{
                color:
                  activeTool === tools[idx].name
                    ? 'var(--tuna-palette-primary-main)'
                    : 'var(--tuna-palette-text-secondary)',
              }}
              disabled={!entity || disabled(entity)}
              title={`${name}`}
              value={name}
              selected={activeTool === tools[idx].name}
              onClick={toggleActive(idx)}
            >
              <Icon fontSize={'medium'} />
            </ToggleButton>
          ))}
        </div>
      ) : null}
    </HBox>
  )
}

export default Sidebar
